import cn from "classnames";
import { useUnreadItems } from "contexts/unreadItemsContext";
import Cr from "cr.svg";
import { organizationDemoPeriodStatusesEnum, sourceTypesEnum } from "helpers/enums";
import { paths } from "helpers/paths";
import { noRolesOtherThan } from "helpers/roles";
import { formattedCount, ymEvent } from "helpers/system";
import Tooltip from "rc-tooltip";
import React, { useEffect, useRef, useState } from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import { Link, NavLink, withRouter } from "react-router-dom";
import { getActiveCount } from "sources/books/tasks";
import { getCountOfCarsWithExternalOutputForPlanning } from "sources/books/trucks";
import connect from "./container";

const SidebarToggleItem = (props) => {
    const [opened, setOpened] = useState(props.location.pathname.includes(props.basePath));

    const toggleGroup = () => {
        const newValue = !opened;
        setOpened(newValue);
    };

    return (
        <>
            <div
                className="sidebar__link cursor-pointer"
                data-onboarding={props.basePath === "planning" ? "sidebar-planning" : undefined}
                onClick={toggleGroup}
            >
                <i className={`sidebar__link__icon sidebar__link__icon--${props.iconClassName}`} />
                <span className="sidebar__link__text">{props.title}</span>
                <span className={cn("sidebar__item-toggler", { "sidebar__item-toggler--opened": opened })} />
                {props.basePath === "chats" && props.hasUnreadChats && (
                    <div className="header__link-count header__link-count--dot" />
                )}
            </div>
            <div className={cn("sidebar__grouped-list", { "sidebar__grouped-list--opened": opened })}>
                {props.children}
            </div>
        </>
    );
};

function Sidebar(props) {
    const [adminSection, setAdminSection] = useState(props.location.pathname.includes("admin"));
    const [countOfCarsWithExternalOutput, setCountOfCarsWithExternalOutput] = useState(0);
    const [activeTasksCount, setActiveTasksCount] = useState(0);
    const { unreadItems } = useUnreadItems();

    const timer = useRef(null);

    useEffect(() => {
        const { roles, profile } = props;
        const { availableFeatureModules } = profile;
        if (
            availableFeatureModules?.includes(sourceTypesEnum.Forwarding) &&
            !noRolesOtherThan(roles, [
                "isGuest",
                "isTechSupport",
                "isAccountManager",
                "isTruckingOrganizationDispatcher",
                "isTruckingOrganizationExtendedViewer",
                "isDiagnostics",
                "isIntegrationManager",
            ])
        ) {
            const updateCounter = () => {
                if (props.organization?.demoPeriod?.status === organizationDemoPeriodStatusesEnum.Expired) {
                    return;
                }
                Promise.all([getActiveCount(), getCountOfCarsWithExternalOutputForPlanning()]).then((counters) => {
                    setActiveTasksCount(counters[0]);
                    setCountOfCarsWithExternalOutput(counters[1]);
                });
            };

            updateCounter();

            timer.current = setInterval(updateCounter, 3 * 60 * 1000); // every 3 minutes
        }
        return () => clearInterval(timer.current);
    }, []);

    const calcIconClassName = (item) => {
        const { path, external, iconClassName } = item;
        let mod;
        if (external) {
            mod = iconClassName;
        } else {
            const splittedPath = (path || "").split("/");
            mod = splittedPath[splittedPath.length - 1];
        }
        return `sidebar__link__icon sidebar__link__icon--${mod}`;
    };

    const renderIconCounters = (item, count, overlay) => {
        const { title } = item;
        return (
            <>
                <i className={calcIconClassName(item)} />
                <span className="sidebar__link__text">{title}</span>
                {overlay ? (
                    <Tooltip overlay={overlay}>
                        <div className="header__link-count">{formattedCount(count)}</div>
                    </Tooltip>
                ) : (
                    <div className="header__link-count">{formattedCount(count)}</div>
                )}
            </>
        );
    };

    const renderButtons = () => {
        const { links, location } = props;
        return links.map((item, i) => {
            if (!!item.inAdminSection !== adminSection) {
                return null;
            }
            if (item.items?.length) {
                return (
                    <SidebarToggleItem
                        key={i}
                        title={item.title}
                        iconClassName={item.iconClassName}
                        location={location}
                        basePath={item.path}
                        hasUnreadChats={Boolean(unreadItems.chats || unreadItems["car-chats"])}
                    >
                        {item.items.map((child) => (
                            <NavLink
                                key={child.path}
                                activeClassName="sidebar__link--active"
                                className="sidebar__link"
                                to={child.path}
                                title={child.title}
                            >
                                {child.title}
                                {child.path === paths.chats && unreadItems.chats > 0 && (
                                    <div className="header__link-count">{formattedCount(unreadItems.chats)}</div>
                                )}
                                {child.path === paths.carChats && unreadItems["car-chats"] > 0 && (
                                    <div className="header__link-count">{formattedCount(unreadItems["car-chats"])}</div>
                                )}
                                {child.path === paths.organizationFixRouteBids &&
                                    unreadItems["fix-route-chats"] > 0 && (
                                        <div className="header__link-count">
                                            {formattedCount(unreadItems["fix-route-chats"])}
                                        </div>
                                    )}
                            </NavLink>
                        ))}
                    </SidebarToggleItem>
                );
            }

            const evaluatedTitle = typeof item.title === "function" ? item.title() : item.title;
            const linkProps = {
                activeClassName: "sidebar__link--active",
                className: "sidebar__link",
                to: item.path,
                title: typeof evaluatedTitle === "string" ? evaluatedTitle : undefined,
                children: (
                    <>
                        <i className={calcIconClassName(item)} />
                        <span className="sidebar__link__text">
                            {typeof item.title === "function"
                                ? item.title(props.profile.availableFeatureModules)
                                : item.title}
                        </span>
                    </>
                ),
            };

            switch (item.path) {
                case paths.notifications:
                    if (unreadItems.notifications) {
                        linkProps.children = renderIconCounters(item, unreadItems.notifications);
                    }
                    break;
                case paths.planning:
                    if (countOfCarsWithExternalOutput) {
                        linkProps.children = renderIconCounters(
                            item,
                            countOfCarsWithExternalOutput,
                            "Количество машин с доступными внешними заказами",
                        );
                    }
                    break;
                case paths.tasks:
                    if (activeTasksCount) {
                        linkProps.children = renderIconCounters(item, activeTasksCount);
                    }
                    break;
            }

            return item.external ? (
                // eslint-disable-next-line react/jsx-no-target-blank
                <a
                    key={i}
                    target="_blank"
                    rel="noopener"
                    href={`${linkProps.to}?serverKey=${window.location.hostname}&token=${localStorage.getItem("token")}`}
                    onClick={() => {
                        if (item.path === paths.forwardingTruckerPlanning) {
                            ymEvent("AccessibleTransport");
                        }
                    }}
                    className="sidebar__link"
                    title={typeof item.title === "function" ? item.title() : item.title}
                >
                    {linkProps.children}
                </a>
            ) : (
                <NavLink exact={item.path === "/bids"} key={item.path} {...linkProps} />
            );
        });
    };

    const renderAdminLinksToggler = () => {
        return (
            <a className="sidebar__link sidebar__link--bottom" onClick={() => setAdminSection(!adminSection)}>
                {adminSection ? (
                    <>
                        <i className="sidebar__link__icon sidebar__link__icon--back" />
                        <span className="sidebar__link__text">Назад</span>
                    </>
                ) : (
                    <>
                        <i className="sidebar__link__icon sidebar__link__icon--dictionaries" />
                        <span className="sidebar__link__text">Админка</span>
                    </>
                )}
            </a>
        );
    };

    const { fullscreen, sidebarOpened, toggleSidebar, roles } = props;
    const { isRoot, isAdmin, isTechSupport, isAccountManager, isIntegrationManager, isDiagnostics } = roles;
    const cName = cn({
        "page-layout__sidebar sidebar": true,
        "sidebar--fullscreen": fullscreen,
        "sidebar--opened": sidebarOpened,
    });

    const isDelko = ["delko.cargorun.ru", "delko.carg.run", "delko.cargo.run"].includes(window.location.hostname);

    return (
        <aside className={cName}>
            <div className="sidebar__logo-wrap">
                <Link className={cn("sidebar__logo", { "sidebar__logo--delko": isDelko })} to={paths.root}></Link>
                <div className="sidebar__close close close--md" onClick={() => toggleSidebar(false)} />
            </div>
            <div className="sidebar__link-wrap">
                <div className="d-flex flex-column justify-content-between h-100">
                    <div className="flex-grow-1 overflow-hidden">
                        <Scrollbars>{renderButtons()}</Scrollbars>
                    </div>
                    <div className="py-2">
                        <div className="sidebar__m-app-wrap">
                            <div className="sidebar__m-app-wrap--header">Мобильнoе приложение</div>
                            <div className="d-flex justify-content-center gap-3 pb-3">
                                <div className="d-flex flex-column justify-content-center align-items-center">
                                    <a
                                        href={paths.mobileApps("director")}
                                        onClick={() => ymEvent("TransitionsToTheStore")}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="sidebar__m-app"
                                    >
                                        <Cr />
                                    </a>
                                    <span className="font-size-10">Директор ТК</span>
                                </div>
                                <div className="d-flex flex-column justify-content-center align-items-center">
                                    <a
                                        href={paths.mobileApps("logist")}
                                        onClick={() => ymEvent("TransitionsToTheStore")}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="sidebar__m-app sidebar__m-app--dark"
                                    >
                                        <Cr />
                                    </a>
                                    <span className="font-size-10">Логист ТК</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    {(isRoot ||
                        isAdmin ||
                        isTechSupport ||
                        isAccountManager ||
                        isIntegrationManager ||
                        isDiagnostics) &&
                        renderAdminLinksToggler()}
                </div>
            </div>
        </aside>
    );
}

export default withRouter(connect(Sidebar));
